import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import FlexLib from '@flex/lib';
import { Grid, Container, Tooltip } from '@mui/material';
import { Topheader } from '../../components/Topheader';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import recurringSchedulesService from '../../services/recurringScheduleService';
import { EditButton } from '../../components/Table/components';
import recurrenceConstants from '../../constants/recurrence';
import { PaymentStatus } from '../../components/Table/components';
const { formatCurrency } = FlexLib.utils.feeService;
const { maskPaymentValue } = FlexLib.utils.common;

const RecurringSchedulesTable = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUserAllowedToExport, setIsUserAllowedToExport] = useState(false);
  const [isUserAllowedToEditRecurringSchedule, setIsUserAllowedToEditRecurringSchedule] = useState(false);
  const [messages, setMessages] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [recurringSchedules, setRecurringSchedules] = useState([]);

  useEffect(() => {
    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setIsUserAllowedToExport(user?.role <= 1 || !!user?.permissions?.exportRecord);
    setIsUserAllowedToEditRecurringSchedule(user?.role <= 3);
  }, []);

  const getRecurringSchedules = (paginationOptions) => {
    const query = spreadPaginationOptions(paginationOptions);

    setIsLoading(true);
    recurringSchedulesService
      .getRecurringSchedules(query)
      .then((response) => {
        if (response.data) {
          setRecurringSchedules(response.data?.recurringSchedules ?? []);
          setRowCount(response.data?.rowCount ?? 0);
        }
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not retrieve recurring schedules. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * @param {string} recurrId - the non-object id field called RECURRID in the database
   */
  const handleEditRecurringSchedule = (recurrId) => {
    history.push('/RecurrenceDetails/' + recurrId);
  };

  const columns = [
    {
      field: 'RECURRID',
      type: 'string',
      headerName: 'Recurring Id',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <span
            data-cy="recurring-schedule-link"
            className="link"
            onClick={(_e) => handleEditRecurringSchedule(params.row.RECURRID, params.row)}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'NAME',
      type: 'string',
      minWidth: 200,
      flex: 1,
      headerName: 'Name',
    },
    {
      field: 'AMOUNT',
      type: 'number',
      headerName: 'Amount',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => <span>{formatCurrency(params.value)}</span>,
    },
    {
      field: 'FREQUENCY',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'Frequency Type',
      valueFormatter: (value) => recurrenceConstants.FREQUENCIES_MAP[value],
    },
    {
      field: 'EMAIL',
      type: 'string',
      headerName: 'Email',
      minWidth: 200,
      flex: 1,
    },
    {
      field: 'FINAL_STATUS',
      type: 'singleSelect',
      valueOptions: ['Updated', 'Deleted', 'Success'],
      headerName: 'Status',
      minWidth: 130,
      flex: 1,
      renderCell: (params) => <PaymentStatus status={params.value} />,
    },
    {
      field: 'MASKED_ACCOUNT_NUMBER',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'Account Number',
      valueFormatter: (value) => maskPaymentValue(value),
    },
    {
      field: 'startDate',
      type: 'date',
      headerName: 'Start Date',
      minWidth: 150,
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    {
      field: 'endDate',
      type: 'date',
      headerName: 'End Date',
      minWidth: 150,
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <>
          {isUserAllowedToEditRecurringSchedule && (
            <EditButton
              id={`edit-${params.row.RECURRID}`}
              onClick={() => handleEditRecurringSchedule(params.row.RECURRID)}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Recurring Billing</h3>
            </Grid>
            <Grid item xs={12}>
              <FlexAlerts messages={messages} />
            </Grid>
            <Grid item xs={12}>
              <TableV2
                rows={recurringSchedules}
                columns={columns}
                fetchData={getRecurringSchedules}
                rowCount={rowCount}
                isLoading={isLoading}
                defaultSortColumn="startDate"
                isExportable={isUserAllowedToExport}
                searchConfig={{ isSearchBar: true }}
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default RecurringSchedulesTable;
